/**
 * ReleaseScreenshotsManager — admin review of release-notes screenshots
 *
 * Workflow:
 *   1. scripts/capture_release_screenshots.py runs (manually or cron) and
 *      headless-Chrome screenshots each release_note's link URLs. PNGs go to
 *      R2 under og-images/release/{id}/{shot_id}.png; rows go to
 *      release_notes_screenshots with status='pending'.
 *   2. Admin opens this tab, sees pending candidates side-by-side with the
 *      release entry context, and approves / rejects / marks-primary each one.
 *   3. Approved + primary screenshot becomes the og:image when the release-notes
 *      page is shared (per-item permalink at /release-notes/r/:id).
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Check, X, Star, ExternalLink, Image as ImageIcon, Loader2, RefreshCw } from 'lucide-react';

const T = {
  bg: '#FAF9F6',
  navy: '#002868',
  red: '#B22234',
  green: '#2b5e49',
  gold: '#C5A55A',
  text: '#1B1B1B',
  textMuted: 'rgba(27,27,27,0.62)',
  border: 'rgba(27,27,27,0.12)',
  cardBg: '#FFFFFF',
  shadow: '0 4px 24px rgba(0,0,0,0.10)',
};

interface Screenshot {
  id: string;
  release_note_id: string;
  source_url: string;
  source_label: string | null;
  storage_url: string;
  width_px: number | null;
  height_px: number | null;
  file_size_bytes: number | null;
  status: 'pending' | 'approved' | 'rejected' | 'obsolete';
  is_primary: boolean;
  captured_at: string;
  release_notes?: {
    title: string;
    kind: string;
    shipped_at: string;
    body_md: string | null;
  };
}

type Filter = 'pending' | 'approved' | 'rejected';

export function ReleaseScreenshotsManager() {
  const [filter, setFilter] = useState<Filter>('pending');
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: shots, isLoading, refetch } = useQuery({
    queryKey: ['release-screenshots', filter],
    queryFn: async () => {
      // Two FKs reference release_notes (release_note_id + the
      // hero_screenshot_id back-reference), so PostgREST needs the FK name
      // spelled out to know which side of the relationship to embed.
      const { data, error } = await supabase
        .from('release_notes_screenshots')
        .select('*, release_notes!release_notes_screenshots_release_note_id_fkey(title, kind, shipped_at, body_md)')
        .eq('status', filter)
        .order('captured_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as Screenshot[];
    },
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, status, is_primary }: { id: string; status?: Screenshot['status']; is_primary?: boolean }) => {
      const patch: Record<string, unknown> = {
        reviewed_at: new Date().toISOString(),
        reviewed_by: 'admin',
      };
      if (status) patch.status = status;
      if (typeof is_primary === 'boolean') patch.is_primary = is_primary;
      const { error } = await supabase.from('release_notes_screenshots').update(patch).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['release-screenshots'] });
    },
  });

  const setPrimaryMut = useMutation({
    mutationFn: async (shot: Screenshot) => {
      // Clear is_primary on all siblings of this release_note, then mark this one.
      const { error: clearErr } = await supabase
        .from('release_notes_screenshots')
        .update({ is_primary: false })
        .eq('release_note_id', shot.release_note_id);
      if (clearErr) throw clearErr;
      const { error: setErr } = await supabase
        .from('release_notes_screenshots')
        .update({ is_primary: true, status: 'approved', reviewed_at: new Date().toISOString(), reviewed_by: 'admin' })
        .eq('id', shot.id);
      if (setErr) throw setErr;
      // Also pin on the parent release_note for fast lookup at render time.
      const { error: pinErr } = await supabase
        .from('release_notes')
        .update({ hero_screenshot_id: shot.id })
        .eq('id', shot.release_note_id);
      if (pinErr) throw pinErr;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['release-screenshots'] });
      toast({ title: 'Primary screenshot set', description: 'Will be used as og:image when shared.' });
    },
  });

  return (
    <div style={{ padding: 24, backgroundColor: T.bg, minHeight: '100vh' }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <header style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h1 style={{ margin: 0, fontFamily: "'Instrument Serif', Georgia, serif", fontSize: 30, fontWeight: 400, color: T.navy }}>
              Release Note Screenshots
            </h1>
            <p style={{ margin: '6px 0 0', color: T.textMuted, fontSize: 14 }}>
              Approve or reject auto-captured screenshots before they become Open Graph share images.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {(['pending', 'approved', 'rejected'] as Filter[]).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: '7px 14px',
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  border: `1px solid ${filter === f ? T.navy : T.border}`,
                  backgroundColor: filter === f ? T.navy : 'transparent',
                  color: filter === f ? '#fff' : T.text,
                  cursor: 'pointer',
                  borderRadius: 4,
                }}
              >
                {f}
              </button>
            ))}
            <button
              onClick={() => refetch()}
              style={{ padding: '7px 10px', border: `1px solid ${T.border}`, background: 'transparent', cursor: 'pointer', borderRadius: 4 }}
              title="Refresh"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </header>

        {isLoading && (
          <div style={{ textAlign: 'center', padding: 60, color: T.textMuted }}>
            <Loader2 size={28} className="animate-spin" /> Loading…
          </div>
        )}

        {!isLoading && (shots ?? []).length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: T.textMuted, background: T.cardBg, border: `1px solid ${T.border}`, borderRadius: 8 }}>
            <ImageIcon size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
            <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>No {filter} screenshots</div>
            {filter === 'pending' && (
              <div style={{ marginTop: 8, fontSize: 13 }}>
                Run <code style={{ background: '#eee', padding: '2px 6px', borderRadius: 3 }}>venv/bin/python3 scripts/capture_release_screenshots.py --since 7d</code> to queue new candidates.
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16 }}>
          {(shots ?? []).map(shot => (
            <article
              key={shot.id}
              style={{
                background: T.cardBg,
                border: `1px solid ${T.border}`,
                borderRadius: 8,
                overflow: 'hidden',
                boxShadow: T.shadow,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}
            >
              {shot.is_primary && (
                <div style={{
                  position: 'absolute', top: 8, right: 8, zIndex: 2,
                  background: T.gold, color: '#000',
                  padding: '3px 8px', fontSize: 10, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.08em', borderRadius: 3,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                  <Star size={10} fill="#000" /> Primary
                </div>
              )}
              <a href={shot.storage_url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', background: '#000' }}>
                <img
                  src={shot.storage_url}
                  alt={shot.source_label || 'Screenshot'}
                  style={{ width: '100%', height: 'auto', display: 'block', aspectRatio: '1200/630', objectFit: 'cover' }}
                  loading="lazy"
                />
              </a>
              <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.text, lineHeight: 1.3 }}>
                  {shot.release_notes?.title || '(release deleted)'}
                </div>
                <div style={{ display: 'flex', gap: 8, fontSize: 11, color: T.textMuted, flexWrap: 'wrap' }}>
                  {shot.release_notes?.kind && (
                    <span style={{ padding: '2px 6px', background: 'rgba(0,40,104,0.08)', borderRadius: 3, color: T.navy, fontWeight: 600 }}>
                      {shot.release_notes.kind}
                    </span>
                  )}
                  {shot.release_notes?.shipped_at && (
                    <span>{new Date(shot.release_notes.shipped_at).toLocaleDateString()}</span>
                  )}
                  {shot.width_px && (
                    <span>{shot.width_px}×{shot.height_px}px</span>
                  )}
                  {shot.file_size_bytes && (
                    <span>{Math.round(shot.file_size_bytes / 1024)}KB</span>
                  )}
                </div>
                <a
                  href={shot.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 11, color: T.navy, textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                >
                  {shot.source_label || 'source'} <ExternalLink size={10} />
                </a>

                {filter === 'pending' && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 'auto', paddingTop: 8 }}>
                    <button
                      onClick={() => setPrimaryMut.mutate(shot)}
                      disabled={setPrimaryMut.isPending}
                      style={{
                        flex: 1, padding: '8px 10px', fontSize: 12, fontWeight: 600,
                        background: T.gold, color: '#000', border: 'none', cursor: 'pointer', borderRadius: 4,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                      }}
                    >
                      <Star size={12} /> Set primary
                    </button>
                    <button
                      onClick={() => updateMut.mutate({ id: shot.id, status: 'approved' })}
                      disabled={updateMut.isPending}
                      style={{
                        flex: 1, padding: '8px 10px', fontSize: 12, fontWeight: 600,
                        background: T.green, color: '#fff', border: 'none', cursor: 'pointer', borderRadius: 4,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                      }}
                    >
                      <Check size={12} /> Approve
                    </button>
                    <button
                      onClick={() => updateMut.mutate({ id: shot.id, status: 'rejected' })}
                      disabled={updateMut.isPending}
                      style={{
                        flex: 1, padding: '8px 10px', fontSize: 12, fontWeight: 600,
                        background: T.red, color: '#fff', border: 'none', cursor: 'pointer', borderRadius: 4,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                      }}
                    >
                      <X size={12} /> Reject
                    </button>
                  </div>
                )}
                {filter === 'approved' && !shot.is_primary && (
                  <button
                    onClick={() => setPrimaryMut.mutate(shot)}
                    disabled={setPrimaryMut.isPending}
                    style={{
                      marginTop: 8, padding: '8px 10px', fontSize: 12, fontWeight: 600,
                      background: T.gold, color: '#000', border: 'none', cursor: 'pointer', borderRadius: 4,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                    }}
                  >
                    <Star size={12} /> Make this the primary
                  </button>
                )}
                {filter !== 'pending' && (
                  <button
                    onClick={() => updateMut.mutate({ id: shot.id, status: 'pending' })}
                    style={{
                      marginTop: 6, padding: '6px 10px', fontSize: 11, color: T.textMuted,
                      background: 'transparent', border: `1px solid ${T.border}`, cursor: 'pointer', borderRadius: 4,
                    }}
                  >
                    Re-queue as pending
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
